import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, DemoBadge } from "@/components/PageHeader";
import { BenefitIcon } from "@/components/BenefitIcon";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, LibraryBig, ShieldCheck, ArrowRight, Plus, Compass } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [household, setHousehold] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/household"),
      api.get("/benefits/categories"),
      api.get("/products"),
      api.get("/enrollments"),
    ])
      .then(([h, c, p, e]) => {
        setHousehold(h.data);
        setCategories(c.data);
        setProducts(p.data);
        setEnrollments(e.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const memberCount = (household?.members?.length || 0) + 1;
  const activeCount = enrollments.filter((e) => e.status === "active").length;
  const availableCount = products.length;
  const progress = availableCount ? Math.min(100, Math.round((enrollments.length / availableCount) * 100)) : 0;
  const firstName = (user?.name || "there").split(" ")[0];

  const stats = [
    { label: "Household members", value: memberCount, icon: Users, to: "/app/household" },
    { label: "Benefits available", value: availableCount, icon: LibraryBig, to: "/app/benefits" },
    { label: "Active coverage", value: activeCount, icon: ShieldCheck, to: "/app/enrollments" },
  ];

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-7xl mx-auto animate-rise">
      <PageHeader
        eyebrow={`Welcome aboard, ${firstName}`}
        title="Your benefits, at a glance"
        subtitle="Everything you need to understand, compare, and enroll — organized around your household."
        action={
          <Link to="/app/benefits">
            <Button className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="dashboard-explore-benefits">
              Explore benefits <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        }
      />

      {/* Enrollment progress banner */}
      <div className="rounded-2xl bg-navy-800 p-8 mb-8 relative overflow-hidden" data-testid="enrollment-progress-banner">
        <Compass className="absolute -right-6 -top-6 w-40 h-40 text-white/5" />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold text-white">
              {activeCount > 0 ? "You've got coverage in place" : "Let's chart your coverage"}
            </h2>
            <p className="text-mist-200 mt-2 max-w-lg">
              {activeCount > 0
                ? `You have ${activeCount} active benefit${activeCount > 1 ? "s" : ""}. Review or adjust anytime.`
                : "Start by reviewing available benefits and making your first election."}
            </p>
          </div>
          <div className="lg:w-72">
            <div className="flex justify-between text-sm text-mist-200 mb-2">
              <span>Elections made</span>
              <span className="text-gold font-bold">{enrollments.length}/{availableCount || 0}</span>
            </div>
            <Progress value={progress} className="h-2 bg-white/15" data-testid="progress-bar" />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Link key={s.label} to={s.to} className="bg-white rounded-xl border border-mist-200 p-6 card-lift block" data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-lg bg-mist-100 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-navy-800" />
                </div>
                <ArrowRight className="w-4 h-4 text-mist-300" />
              </div>
              <div className="mt-4 font-display text-4xl font-bold text-navy-900">
                {loading ? <Skeleton className="h-9 w-12" /> : s.value}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
            </Link>
          );
        })}
      </div>

      {/* Household + Categories */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white rounded-xl border border-mist-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold text-navy-900">Your household</h3>
            <Link to="/app/household"><Button variant="ghost" size="sm" className="text-gold-dark" data-testid="manage-household">Manage</Button></Link>
          </div>
          <div className="space-y-3">
            <MemberRow name={user?.name} rel="You (employee)" />
            {loading ? <Skeleton className="h-10 w-full" /> :
              household?.members?.map((m) => (
                <MemberRow key={m.id} name={`${m.first_name} ${m.last_name}`} rel={m.relationship.replace("_", " ")} />
              ))}
            <Link to="/app/household" className="flex items-center gap-2 text-sm text-gold-dark font-semibold pt-2 hover:underline" data-testid="add-member-link">
              <Plus className="w-4 h-4" /> Add a family member
            </Link>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-xl border border-mist-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-xl font-bold text-navy-900">Benefit categories</h3>
              <DemoBadge />
            </div>
            <Link to="/app/benefits"><Button variant="ghost" size="sm" className="text-gold-dark" data-testid="view-all-benefits">View all</Button></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {loading ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />) :
              categories.slice(0, 6).map((c) => (
                <Link key={c.id} to={`/app/benefits/${c.key}`} data-testid={`category-tile-${c.key}`}
                  className="flex items-start gap-3 p-4 rounded-lg border border-mist-200 hover:border-gold hover:bg-mist-100 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-navy-800 flex items-center justify-center shrink-0">
                    <BenefitIcon name={c.icon} className="w-[18px] h-[18px] text-gold" />
                  </div>
                  <div>
                    <p className="font-semibold text-navy-900 text-sm">{c.name}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{c.short_description}</p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MemberRow({ name, rel }) {
  const initials = (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("");
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-navy-800 text-gold flex items-center justify-center font-bold text-xs">{initials}</div>
      <div>
        <p className="text-sm font-semibold text-navy-900">{name}</p>
        <p className="text-xs text-muted-foreground capitalize">{rel}</p>
      </div>
    </div>
  );
}
